/* Multi-select tag picker with create-new-tag support. Uses shadcn Combobox (chips variant). */

import { useState, useRef } from "react";
import {
  Combobox,
  ComboboxChips,
  ComboboxChip,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxList,
  ComboboxItem,
  useComboboxAnchor,
} from "@/components/ui/combobox";
import { Plus } from "lucide-react";

interface SourceTagSelectProps {
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  availableTags: string[];
  onCreateTag: (tag: string) => Promise<void>;
  portalContainer?: React.RefObject<HTMLElement | null>;
}

export function SourceTagSelect({
  selectedTags,
  onTagsChange,
  availableTags,
  onCreateTag,
  portalContainer,
}: SourceTagSelectProps) {
  const [inputValue, setInputValue] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useComboboxAnchor();
  const inputRef = useRef<HTMLInputElement>(null);

  const trimmedInput = inputValue.trim();
  const inputLower = trimmedInput.toLowerCase();

  const filteredTags = availableTags
    .filter((tag) => !selectedTags.includes(tag))
    .filter((tag) => !inputLower || tag.toLowerCase().includes(inputLower));

  const matchesExisting = availableTags.some(
    (t) => t.toLowerCase() === inputLower,
  );
  const showCreateOption = trimmedInput.length > 0 && !matchesExisting;

  async function handleCreateTag() {
    if (!trimmedInput || isCreating) return;
    setIsCreating(true);
    try {
      await onCreateTag(trimmedInput);
      onTagsChange([...selectedTags, trimmedInput]);
      setInputValue("");
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <Combobox
      multiple
      value={selectedTags}
      onValueChange={onTagsChange}
      inputValue={inputValue}
      onInputValueChange={setInputValue}
      open={isOpen}
      onOpenChange={setIsOpen}
      filter={null}
    >
      <ComboboxChips ref={anchorRef}>
        {selectedTags.map((tag) => (
          <ComboboxChip key={tag}>
            {tag}
          </ComboboxChip>
        ))}
        <ComboboxChipsInput
          ref={inputRef}
          placeholder={selectedTags.length === 0 ? "Add tags..." : ""}
          onFocus={() => setIsOpen(true)}
        />
      </ComboboxChips>
      <ComboboxContent anchor={anchorRef} container={portalContainer?.current}>
        <ComboboxList>
          {filteredTags.map((tag) => (
            <ComboboxItem key={tag} value={tag}>
              {tag}
            </ComboboxItem>
          ))}
          {showCreateOption && (
            <ComboboxItem
              value={trimmedInput}
              onSelect={handleCreateTag}
            >
              <Plus className="size-4" />
              Create &ldquo;{trimmedInput}&rdquo;
            </ComboboxItem>
          )}
          {filteredTags.length === 0 && !showCreateOption && (
            <p className="text-muted-foreground py-2 text-center text-sm">
              {trimmedInput ? "No matching tags" : "No tags yet"}
            </p>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
